import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from "../../utils/Enums";
import { apiClient } from "../../utils/ApiClient";
import { NewOfflineReasonFormProps } from "../../components/offlineReasons/NewOfflineReasonScreen";
import { ModifyOfflineReasonFormProps } from "../../components/offlineReasons/ModifyOfflineReasonScreen";
import OfflineReason from "../../models/OfflineReason";

interface OfflineReasonsState {
    offlineReasons: OfflineReason[];
    currentOfflineReason?: OfflineReason | null;
    getOfflineReasonsStatus: RequestStatus;
    getCurrentOfflineReason: RequestStatus;
    createOfflineReasonStatus: RequestStatus;
    updateOfflineReasonStatus: RequestStatus;
    deleteOfflineReasonStatus: RequestStatus;
    errors: string[];
}

const initialState: OfflineReasonsState = {
    offlineReasons: [],
    currentOfflineReason: null,
    getOfflineReasonsStatus: RequestStatus.Idle,
    getCurrentOfflineReason: RequestStatus.Idle,
    createOfflineReasonStatus: RequestStatus.Idle,
    updateOfflineReasonStatus: RequestStatus.Idle,
    deleteOfflineReasonStatus: RequestStatus.Idle,
    errors: [],
};

export const getOfflineReasons = createAsyncThunk(
    'offlineReasons/getOfflineReasons',
    async (_, { rejectWithValue }) => {
        const response = await apiClient.get('offline-reasons');
        return response.data;
    }
);

export const createOfflineReason = createAsyncThunk(
    'offlineReasons/createOfflineReason',
    async (data: NewOfflineReasonFormProps, { rejectWithValue }) => {
        const response = await apiClient.post('offline-reasons', data);
        return response.data;
    }
);

export const getCurrentOfflineReason = createAsyncThunk(
    'offlineReasons/getCurrentOfflineReason',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.get(`offline-reasons/${id}`);
        return response.data;
    }
);

export const updateOfflineReason = createAsyncThunk(
    'offlineReasons/updateOfflineReason',
    async ({ id, data }: { id: string | undefined, data: ModifyOfflineReasonFormProps }, { rejectWithValue }) => {
        const response = await apiClient.put(`offline-reasons/${id}`, data);
        return response.data;
    }
);

export const deleteOfflineReason = createAsyncThunk(
    'offlineReasons/deleteOfflineReason',
    async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
        const response = await apiClient.delete(`offline-reasons/${id}`);
        return response.data;
    }
);

const offlineReasonsSlice = createSlice({
    name: 'offlineReasons',
    initialState,
    reducers: {
        resetCreateOfflineReasonStatus: (state) => {
            state.createOfflineReasonStatus = RequestStatus.Idle;
            state.currentOfflineReason = null;
        },
        resetUpdateOfflineReasonStatus: (state) => {
            state.updateOfflineReasonStatus = RequestStatus.Idle;
            state.currentOfflineReason = null;
        },
        resetDeleteOfflineReasonStatus: (state) => {
            state.deleteOfflineReasonStatus = RequestStatus.Idle;
            state.currentOfflineReason = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getOfflineReasons.pending, (state, action) => {
                state.getOfflineReasonsStatus = RequestStatus.Loading;
            })
            .addCase(getOfflineReasons.fulfilled, (state, action) => {
                state.getOfflineReasonsStatus = RequestStatus.Success;
                state.offlineReasons = action.payload;
            })
            .addCase(getOfflineReasons.rejected, (state, action) => {
                state.getOfflineReasonsStatus = RequestStatus.Failed;
            })

            .addCase(createOfflineReason.pending, (state, action) => {
                state.createOfflineReasonStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createOfflineReason.fulfilled, (state, action) => {
                state.createOfflineReasonStatus = RequestStatus.Success;
            })
            .addCase(createOfflineReason.rejected, (state, action) => {
                state.createOfflineReasonStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(updateOfflineReason.pending, (state, action) => {
                state.updateOfflineReasonStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateOfflineReason.fulfilled, (state, action) => {
                state.updateOfflineReasonStatus = RequestStatus.Success;
            })
            .addCase(updateOfflineReason.rejected, (state, action) => {
                state.updateOfflineReasonStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteOfflineReason.pending, (state, action) => {
                state.deleteOfflineReasonStatus = RequestStatus.Loading;
            })
            .addCase(deleteOfflineReason.fulfilled, (state, action) => {
                state.deleteOfflineReasonStatus = RequestStatus.Success;
            })
            .addCase(deleteOfflineReason.rejected, (state, action) => {
                state.deleteOfflineReasonStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentOfflineReason.pending, (state, action) => {
                state.getCurrentOfflineReason = RequestStatus.Loading;
            })
            .addCase(getCurrentOfflineReason.fulfilled, (state, action) => {
                state.getCurrentOfflineReason = RequestStatus.Success;
                state.currentOfflineReason = action.payload;
            })
            .addCase(getCurrentOfflineReason.rejected, (state, action) => {
                state.getCurrentOfflineReason = RequestStatus.Failed;
            });
    },
});

export const {
    resetCreateOfflineReasonStatus,
    resetUpdateOfflineReasonStatus,
    resetDeleteOfflineReasonStatus
} = offlineReasonsSlice.actions;

export default offlineReasonsSlice.reducer;